import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch } from 'react-icons/fa';
import axios from 'axios';

const Biler = () => {
  const [biler, setBiler] = useState([]);
  const [stationer, setStationer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBil, setEditingBil] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const pageSizeOptions = [20, 50, 100, 200];
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    internt_nummer: '',
    nummerplade: '',
    model: '',
    vogn_type: '',
    status: 'I drift',
    station_id: '',
    type: 'Almindelig'
  });
  const [apiLookup, setApiLookup] = useState({ loading: false, error: '', data: null });
  const [stationQuery, setStationQuery] = useState('');
  const [stationOpen, setStationOpen] = useState(false);
  const isAdmin = (() => {
    try { return localStorage.getItem('userRole') === 'admin'; } catch (_) { return false; }
  })();

  const vognTypeOptions = useMemo(() => {
    const unique = new Set();
    biler.forEach((bil) => {
      const type = (bil?.vogn_type || '').trim();
      if (type) unique.add(type);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'da', { sensitivity: 'base' }));
  }, [biler]);

  useEffect(() => {
    fetchBiler();
    fetchStationer();
  }, []);


  const fetchBiler = async () => {
    try {
      const response = await axios.get('/api/biler');
      setBiler(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching biler:', error);
      setLoading(false);
    }
  };

  const fetchStationer = async () => {
    try {
      const response = await axios.get('/api/stationer');
      setStationer(response.data);
    } catch (error) {
      console.error('Error fetching stationer:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBil) {
        await axios.put(`/api/biler/${editingBil.id}`, formData);
      } else {
        const payload = { ...formData };
        if (apiLookup.data) {
          const d = apiLookup.data;
          payload['stelnummer'] = d['stelnummer'] ?? null;
          payload['mærke'] = d['mærke'] ?? null;
          payload['art'] = d['art'] ?? null;
          payload['første_registreringsdato'] = d['første_registreringsdato'] ?? null;
          payload['anvendelse'] = d['anvendelse'] ?? null;
          payload['mot_type'] = d['mot_type'] ?? null;
          payload['mot_dato'] = d['mot_dato'] ?? null;
          payload['mot_resultat'] = d['mot_resultat'] ?? null;
          payload['mot_status'] = d['mot_status'] ?? null;
          payload['mot_kilometerstand'] = d['mot_kilometerstand'] ?? null;
          // Brug model fra formular hvis udfyldt, ellers fra opslag
          if (!payload.model && d['model']) payload.model = d['model'];
        }
        const res = await axios.post('/api/biler', payload);
        const newId = res.data?.id;
        if (newId) {
          setShowModal(false);
          setEditingBil(null);
          resetForm();
          navigate(`/biler/profil/${newId}`);
          return;
        }
      }
      setShowModal(false);
      setEditingBil(null);
      resetForm();
      fetchBiler();
    } catch (error) {
      console.error('Error saving bil:', error);
    }
  };

  const handleFetchDMR = async () => {
    if (!formData.nummerplade.trim()) {
      setApiLookup({ loading: false, error: 'Indtast en nummerplade', data: null });
      return;
    }
    try {
      setApiLookup({ loading: true, error: '', data: null });
      const res = await axios.post('/api/biler/dmr-lookup', { nummerplade: formData.nummerplade.trim() });
      const data = res.data?.data || null;
      if (data) {
        setApiLookup({ loading: false, error: '', data });
        // Merge relevante felter ind i formData hvis tomme
        setFormData(prev => ({
          ...prev,
          model: prev.model || data.model || prev.model,
          vogn_type: prev.vogn_type || data.art || prev.vogn_type,
          // Bevar nummerplade som indtastet
        }));
      } else {
        setApiLookup({ loading: false, error: 'Ingen data retur fra DMR', data: null });
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Fejl ved opslag';
      setApiLookup({ loading: false, error: msg, data: null });
    }
  };

  const handleEdit = (bil) => {
    setEditingBil(bil);
    setFormData({
      internt_nummer: bil.internt_nummer,
      nummerplade: bil.nummerplade || '',
      model: bil.model || '',
      vogn_type: bil.vogn_type || '',
      status: bil.status,
      station_id: bil.station_id || '',
      type: bil.type
    });
    const st = stationer.find((s) => s.id === bil.station_id);
    if (st) {
      setStationQuery(st.stationsnummer ? `${st.stationsnummer} - ${st.navn}` : st.navn);
    } else {
      setStationQuery('');
    }
    setShowModal(true);
  };


  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne bil?')) {
      try {
        await axios.delete(`/api/biler/${id}`);
        fetchBiler();
      } catch (error) {
        console.error('Error deleting bil:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      internt_nummer: '',
      nummerplade: '',
      model: '',
      vogn_type: '',
      status: 'I drift',
      station_id: '',
      type: 'Almindelig'
    });
    setApiLookup({ loading: false, error: '', data: null });
    setStationQuery('');
    setStationOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'I drift': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'På værksted': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Udlånt': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Reserve': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Til salg': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'På auktion': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/80 dark:text-purple-100';
      case 'Solgt': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterType, pageSize]);

  const filteredBiler = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();
    return biler.filter(bil => {
      const matchesSearch = !searchValue ||
        (bil.internt_nummer && bil.internt_nummer.toLowerCase().includes(searchValue)) ||
        (bil.nummerplade && bil.nummerplade.toLowerCase().includes(searchValue)) ||
        (bil.model && bil.model.toLowerCase().includes(searchValue));

      const matchesStatus = !filterStatus || bil.status === filterStatus;
      const matchesType = !filterType || bil.type === filterType;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [biler, searchTerm, filterStatus, filterType]);

  const totalPages = Math.max(1, Math.ceil(filteredBiler.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedBiler = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredBiler.slice(start, start + pageSize);
  }, [filteredBiler, currentPage, pageSize]);

  const hasResults = filteredBiler.length > 0;
  const startItem = hasResults ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = hasResults ? startItem + paginatedBiler.length - 1 : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Biler</h1>
        {isAdmin && (
          <button
            onClick={() => {
              setEditingBil(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <FaPlus /> Tilføj ny bil
          </button>
        )}
      </div>

      {/* Søg og filtre */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Søg efter bil..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-falck-blue"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
          >
            <option value="">Alle statusser</option>
            <option value="I drift">I drift</option>
            <option value="På værksted">På værksted</option>
            <option value="Udlånt">Udlånt</option>
            <option value="Reserve">Reserve</option>
            <option value="Til salg">Til salg</option>
            <option value="Solgt">Solgt</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue"
          >
            <option value="">Alle typer</option>
            <option value="Almindelig">Almindelig</option>
            <option value="Lånebil">Lånebil</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">
            {filteredBiler.length} af {biler.length} biler
          </div>
        </div>
      </div>

      {/* Biler tabel */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
        <div className="scroll-fade-x"><div className="scroll-area">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-gray-900 dark:text-white">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vogn nr.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Vogn type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nummerplade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Station
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Handlinger
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedBiler.map((bil) => (
                <tr key={bil.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/biler/profil/${bil.id}`)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {bil.internt_nummer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bil.vogn_type || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bil.nummerplade || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bil.model || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bil.status)}`}>
                      {bil.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bil.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bil.station_navn || '-'}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/biler/profil/${bil.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Se profil"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleEdit(bil)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Rediger"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(bil.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Slet"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <span>Vis</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-falck-blue bg-white dark:bg-gray-800"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span>pr. side</span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {hasResults
              ? `Viser ${startItem}-${endItem} af ${filteredBiler.length} biler (total ${biler.length})`
              : `Ingen biler matcher filtrene (total ${biler.length})`}
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3 text-xs sm:text-sm text-gray-700 dark:text-gray-200">
            {currentPage > 1 && hasResults && (
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Første
              </button>
            )}
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || !hasResults}
              className={`px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 transition ${currentPage === 1 || !hasResults ? 'opacity-60 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Forrige
            </button>
            <span className="whitespace-nowrap">Side {hasResults ? currentPage : 0} af {hasResults ? totalPages : 0}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || !hasResults}
              className={`px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 transition ${currentPage === totalPages || !hasResults ? 'opacity-60 cursor-not-allowed' : 'bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              Næste
            </button>
            {currentPage < totalPages && hasResults && (
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                Sidste
              </button>
            )}
          </div>
        </div>
      </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-gray-600/60 px-3 sm:px-4 py-6 overflow-y-auto">
            <div className="w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
              <div className="mt-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {editingBil ? 'Rediger bil' : 'Tilføj ny bil'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Internt nummer *</label>
                  <input
                    type="text"
                    required
                    value={formData.internt_nummer}
                    onChange={(e) => setFormData({...formData, internt_nummer: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Nummerplade</label>
                  <input
                    type="text"
                    value={formData.nummerplade}
                    onChange={(e) => setFormData({...formData, nummerplade: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleFetchDMR}
                    className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 disabled:opacity-60"
                    disabled={apiLookup.loading}
                  >
                  {apiLookup.loading ? 'Henter...' : 'Hent data'}
                </button>
                {apiLookup.error && (
                  <span className="text-sm text-red-600">{apiLookup.error}</span>
                )}
              </div>
                {apiLookup.data && (
                  <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
                  <div><strong>Mærke:</strong> {apiLookup.data['mærke'] || '-'}</div>
                  <div><strong>Model:</strong> {apiLookup.data['model'] || '-'}</div>
                  <div><strong>Stelnummer:</strong> {apiLookup.data['stelnummer'] || '-'}</div>
                  <div><strong>Første registreringsdato:</strong> {apiLookup.data['første_registreringsdato'] || '-'}</div>
                  <div><strong>Art:</strong> {apiLookup.data['art'] || '-'}</div>
                  <div><strong>Anvendelse:</strong> {apiLookup.data['anvendelse'] || '-'}</div>
                  <div><strong>Synstype:</strong> {apiLookup.data['mot_type'] || '-'}</div>
                  <div><strong>Synsdato:</strong> {apiLookup.data['mot_dato'] || '-'}</div>
                  <div><strong>Synsresultat:</strong> {apiLookup.data['mot_resultat'] || '-'}</div>
                  <div><strong>Synsstatus:</strong> {apiLookup.data['mot_status'] || '-'}</div>
                  <div><strong>Kilometerstand:</strong> {apiLookup.data['mot_kilometerstand'] || '-'}</div>
                </div>
              )}
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Vogn type</label>
                    <input
                      type="text"
                      list="vogn-type-options"
                      value={formData.vogn_type}
                      onChange={(e) => setFormData({...formData, vogn_type: e.target.value})}
                      placeholder={vognTypeOptions.length ? 'Skriv eller vælg en eksisterende type' : ''}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                    />
                    {vognTypeOptions.length > 0 && (
                      <datalist id="vogn-type-options">
                        {vognTypeOptions.map((type) => (
                          <option key={type} value={type} />
                        ))}
                      </datalist>
                    )}
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  >
                    <option value="I drift">I drift</option>
                    <option value="På værksted">På værksted</option>
                    <option value="Udlånt">Udlånt</option>
                    <option value="Reserve">Reserve</option>
                    <option value="Til salg">Til salg</option>
                    <option value="Solgt">Solgt</option>
                  </select>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  >
                    <option value="Almindelig">Almindelig</option>
                    <option value="Lånebil">Lånebil</option>
                  </select>
                </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Station</label>
                  <input
                    type="text"
                    value={stationQuery}
                    onChange={(e) => {
                      setStationQuery(e.target.value);
                      setFormData({ ...formData, station_id: '' });
                    }}
                    onFocus={() => setStationOpen(true)}
                    onBlur={() => setTimeout(() => setStationOpen(false), 150)}
                    placeholder="Søg efter station..."
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                  {stationOpen && stationQuery && (
                    <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg">
                      {stationer
                        .filter((s) => {
                          const label = `${s.stationsnummer ? s.stationsnummer + ' - ' : ''}${s.navn}`.toLowerCase();
                          return label.includes(stationQuery.toLowerCase());
                        })
                        .slice(0, 20)
                        .map((s) => (
                          <div
                            key={s.id}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setFormData({ ...formData, station_id: s.id });
                              setStationQuery(s.stationsnummer ? `${s.stationsnummer} - ${s.navn}` : s.navn);
                              setStationOpen(false);
                            }}
                          >
                            {s.stationsnummer ? `${s.stationsnummer} - ${s.navn}` : s.navn}
                          </div>
                        ))}
                    </div>
                  )}
                  {formData.station_id && (
                    <p className="mt-1 text-xs text-gray-500">Valgt station-id: {formData.station_id}</p>
                  )}
                </div>
                  <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingBil(null);
                      resetForm();
                    }}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-800 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-falck-blue rounded-md hover:bg-blue-700"
                  >
                    {editingBil ? 'Opdater' : 'Opret'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Biler;
